import { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { parseDate } from '@/lib/localizedDateUtils';
import { Link } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  Check,
  X,
  ExternalLink,
  MoreHorizontal,
  Clock,
  AlertTriangle,
  CheckCircle,
  User,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  DollarSign,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

import { MoneyDisplay, AgingBadge, getAgingFaixa } from '@/components/finance';
import type { ReceivablePayment } from '@/hooks/finance/useReceivables';
import type { ColumnDefinition, SortConfig } from '@/hooks/useSalesReportColumns';
import { getEffectivePaymentStatus } from '@/lib/paymentStatus';

const STATUS_CONFIG = {
  pending: { label: 'Pendente', variant: 'secondary' as const, icon: Clock },
  overdue: { label: 'Atrasado', variant: 'destructive' as const, icon: AlertTriangle },
  paid: { label: 'Pago', variant: 'default' as const, icon: CheckCircle },
  cancelled: { label: 'Cancelado', variant: 'outline' as const, icon: X },
};

export interface ClientGroup {
  clientName: string;
  contactId: string;
  payments: ReceivablePayment[];
  totalAberto: number;
  totalAtrasado: number;
  quantidadePagamentos: number;
  maiorAtraso: number;
}

interface GroupedByClientTableProps {
  groups: ClientGroup[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleSelectGroup: (paymentIds: string[]) => void;
  onOpenPay: (payment: ReceivablePayment) => void;
  onOpenCancel: (payment: ReceivablePayment) => void;
  onOpenCharge: (payment: ReceivablePayment) => void;
  // Dynamic columns props
  groupColumns: ColumnDefinition[];
  paymentColumns: ColumnDefinition[];
  groupSortConfig: SortConfig | null;
  paymentSortConfig: SortConfig | null;
  onGroupSort: (columnId: string) => void;
  onPaymentSort: (columnId: string) => void;
}

const getPaymentStatus = (payment: ReceivablePayment): keyof typeof STATUS_CONFIG => {
  const effective = getEffectivePaymentStatus({
    status: payment.status,
    dueDate: payment.due_date,
    amount: payment.amount,
    paidAmount: payment.paid_amount,
    balance: payment.valor_aberto,
  });
  if (effective === 'paid' || effective === 'cancelled' || effective === 'overdue') return effective;
  if (payment.dias_atraso > 0) return 'overdue';
  return 'pending';
};

const SortIcon = ({ columnId, sortConfig }: { columnId: string; sortConfig: SortConfig | null }) => {
  if (sortConfig?.column !== columnId) {
    return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />;
  }
  return sortConfig.direction === 'asc' 
    ? <ArrowUp className="h-3 w-3 ml-1" />
    : <ArrowDown className="h-3 w-3 ml-1" />;
};

// Helper function to compare values for sorting
const comparePaymentValues = (a: unknown, b: unknown, direction: 'asc' | 'desc'): number => {
  const multiplier = direction === 'asc' ? 1 : -1;
  
  // Handle dates
  if (a instanceof Date && b instanceof Date) {
    return (a.getTime() - b.getTime()) * multiplier;
  }
  
  // Handle numbers
  if (typeof a === 'number' && typeof b === 'number') {
    return (a - b) * multiplier;
  }
  
  // Handle strings
  if (typeof a === 'string' && typeof b === 'string') {
    return a.localeCompare(b, 'pt-BR') * multiplier;
  }
  
  return 0;
};

const getPaymentSortValue = (payment: ReceivablePayment, columnId: string): unknown => {
  switch (columnId) {
    case 'due_date':
      return parseDate(payment.due_date);
    case 'valor_aberto':
      return payment.valor_aberto;
    case 'dias_atraso':
      return payment.dias_atraso;
    case 'description':
      return payment.description || '';
    case 'vendedor':
      return payment.vendedor || '';
    case 'status':
      return getPaymentStatus(payment);
    default:
      return '';
  }
};

// Component to render sorted payments
interface SortedPaymentsBodyProps {
  payments: ReceivablePayment[];
  paymentColumns: ColumnDefinition[];
  paymentSortConfig: SortConfig | null;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onOpenPay: (payment: ReceivablePayment) => void;
  onOpenCancel: (payment: ReceivablePayment) => void;
  onOpenCharge: (payment: ReceivablePayment) => void;
  renderPaymentCellValue: (payment: ReceivablePayment, column: ColumnDefinition) => React.ReactNode;
  getColumnAlignment: (align?: 'left' | 'right' | 'center') => string;
}

function SortedPaymentsBody({
  payments,
  paymentColumns,
  paymentSortConfig,
  selectedIds,
  onToggleSelect,
  onOpenPay,
  onOpenCancel,
  onOpenCharge,
  renderPaymentCellValue,
  getColumnAlignment,
}: SortedPaymentsBodyProps) {
  const sortedPayments = useMemo(() => {
    if (!paymentSortConfig) return payments;
    
    return [...payments].sort((a, b) => {
      const aValue = getPaymentSortValue(a, paymentSortConfig.column);
      const bValue = getPaymentSortValue(b, paymentSortConfig.column);
      return comparePaymentValues(aValue, bValue, paymentSortConfig.direction);
    });
  }, [payments, paymentSortConfig]);

  return (
    <TableBody>
      {sortedPayments.map((payment) => {
        const status = getPaymentStatus(payment);

        return (
          <TableRow key={payment.payment_id} className="hover:bg-muted/10">
            <TableCell>
              <Checkbox
                checked={selectedIds.has(payment.payment_id)}
                onCheckedChange={() => onToggleSelect(payment.payment_id)}
              />
            </TableCell>
            {paymentColumns.map((column) => (
              <TableCell key={column.id} className={getColumnAlignment(column.align)}>
                {renderPaymentCellValue(payment, column)}
              </TableCell>
            ))}
            <TableCell>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {status !== 'paid' && status !== 'cancelled' && (
                    <>
                      <DropdownMenuItem onClick={() => onOpenCharge(payment)}>
                        <DollarSign className="h-4 w-4 mr-2" />
                        Cobrar Cliente
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => onOpenPay(payment)}>
                        <Check className="h-4 w-4 mr-2" />
                        Registrar Pagamento
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => onOpenCancel(payment)}
                        className="text-destructive"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Cancelar
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuItem asChild>
                    <Link to={`/deal-detail?id=${payment.deal_id}`}>
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Ver Deal
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </TableRow>
        );
      })}
    </TableBody>
  );
}

export function GroupedByClientTable({
  groups,
  selectedIds,
  onToggleSelect,
  onToggleSelectGroup,
  onOpenPay,
  onOpenCancel,
  onOpenCharge,
  groupColumns,
  paymentColumns,
  groupSortConfig,
  paymentSortConfig,
  onGroupSort,
  onPaymentSort,
}: GroupedByClientTableProps) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (contactId: string) => {
    const newSet = new Set(expandedGroups);
    if (newSet.has(contactId)) {
      newSet.delete(contactId);
    } else {
      newSet.add(contactId);
    }
    setExpandedGroups(newSet);
  };

  const isGroupSelected = (group: ClientGroup): boolean => {
    return group.payments.every(p => selectedIds.has(p.payment_id));
  };

  const isGroupPartiallySelected = (group: ClientGroup): boolean => {
    const selected = group.payments.filter(p => selectedIds.has(p.payment_id));
    return selected.length > 0 && selected.length < group.payments.length;
  };

  const renderGroupCellValue = (group: ClientGroup, column: ColumnDefinition) => {
    switch (column.id) {
      case 'clientName':
        return (
          <div className="flex items-center gap-2 min-w-0">
            <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <span className="font-medium text-foreground truncate">{group.clientName}</span>
          </div>
        );
      case 'quantidadePagamentos':
        return <span className="font-medium">{group.quantidadePagamentos}</span>;
      case 'totalAberto':
        return <MoneyDisplay value={group.totalAberto} currency="USD" />;
      case 'totalAtrasado':
        return (
          <span className={group.totalAtrasado > 0 ? 'text-destructive' : 'text-muted-foreground'}>
            <MoneyDisplay value={group.totalAtrasado} currency="USD" />
          </span>
        );
      case 'maiorAtraso':
        return (
          <span className={group.maiorAtraso > 0 ? 'text-destructive font-medium' : 'text-muted-foreground'}>
            {group.maiorAtraso > 0 ? `${group.maiorAtraso} dias` : '-'}
          </span>
        );
      default:
        return null;
    }
  };

  const renderPaymentCellValue = (payment: ReceivablePayment, column: ColumnDefinition) => {
    const status = getPaymentStatus(payment);
    const config = STATUS_CONFIG[status];
    const StatusIcon = config.icon;

    switch (column.id) {
      case 'description':
        return (
          <Link
            to={`/deal-detail?id=${payment.deal_id}`}
            className="text-primary hover:underline flex items-center gap-1"
          >
            {payment.description}
            <ExternalLink className="h-3 w-3" />
          </Link>
        );
      case 'valor_aberto':
        return (
          <MoneyDisplay
            value={payment.valor_aberto}
            currency={payment.currency as 'USD' | 'BRL'}
          />
        );
      case 'due_date':
        return format(parseDate(payment.due_date)!, 'dd/MM/yyyy', { locale: ptBR });
      case 'dias_atraso':
        if (payment.dias_atraso > 0) {
          return <span className="text-destructive font-medium">{payment.dias_atraso} dias</span>;
        } else if (payment.dias_atraso < 0) {
          return <span className="text-muted-foreground">{Math.abs(payment.dias_atraso)}d</span>;
        }
        return <span className="text-amber-600 font-medium">Hoje</span>;
      case 'faixa':
        return <AgingBadge faixa={getAgingFaixa(payment.dias_atraso)} />;
      case 'status':
        return (
          <Badge variant={config.variant} className="gap-1">
            <StatusIcon className="h-3 w-3" />
            {config.label}
          </Badge>
        );
      case 'vendedor':
        return <span className="text-muted-foreground text-sm">{payment.vendedor}</span>;
      default:
        return null;
    }
  };

  const getColumnAlignment = (align?: 'left' | 'right' | 'center') => {
    switch (align) {
      case 'right': return 'text-right';
      case 'center': return 'text-center';
      default: return 'text-left';
    }
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            <TableHead className="w-[40px]"></TableHead>
            <TableHead className="w-[40px]"></TableHead>
            {groupColumns.map((column) => (
              <TableHead
                key={column.id}
                className={`${getColumnAlignment(column.align)} ${column.sortable ? 'cursor-pointer hover:bg-muted/80' : ''}`}
                onClick={() => column.sortable && onGroupSort(column.id)}
              >
                <div className={`flex items-center gap-1 ${column.align === 'right' ? 'justify-end' : column.align === 'center' ? 'justify-center' : ''}`}>
                  {column.labelKey}
                  {column.sortable && <SortIcon columnId={column.id} sortConfig={groupSortConfig} />}
                </div>
              </TableHead>
            ))}
            <TableHead className="w-[80px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.map((group) => {
            const isExpanded = expandedGroups.has(group.contactId);
            const groupSelected = isGroupSelected(group);
            const partiallySelected = isGroupPartiallySelected(group);

            return (
              <Collapsible
                key={group.contactId}
                open={isExpanded}
                onOpenChange={() => toggleGroup(group.contactId)}
                asChild
              >
                <>
                  {/* Group Header Row */}
                  <TableRow className="bg-muted/30 hover:bg-muted/50">
                    <TableCell className="w-[40px]">
                      <Checkbox
                        checked={groupSelected}
                        ref={(ref) => {
                          if (ref && partiallySelected) {
                            ref.dataset.state = 'indeterminate';
                          }
                        }}
                        onCheckedChange={() => {
                          const paymentIds = group.payments.map(p => p.payment_id);
                          onToggleSelectGroup(paymentIds);
                        }}
                      />
                    </TableCell>
                    <TableCell className="w-[40px]">
                      <CollapsibleTrigger asChild>
                        <Button variant="ghost" size="sm" className="p-0 h-6 w-6">
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </Button>
                      </CollapsibleTrigger>
                    </TableCell>
                    {groupColumns.map((column) => (
                      <TableCell key={column.id} className={getColumnAlignment(column.align)}>
                        {renderGroupCellValue(group, column)}
                      </TableCell>
                    ))}
                    <TableCell className="text-right">
                      <CollapsibleTrigger asChild>
                        <Button variant="outline" size="sm" className="h-7 text-xs">
                          {isExpanded ? 'Fechar' : 'Expandir'}
                        </Button>
                      </CollapsibleTrigger>
                    </TableCell>
                  </TableRow>

                  {/* Expanded Payment Details */}
                  <CollapsibleContent asChild>
                    <TableRow>
                      <TableCell colSpan={groupColumns.length + 3} className="p-0 bg-background">
                        <div className="ml-10 my-2 mr-4 border rounded-lg overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-muted/20">
                                <TableHead className="w-[40px]"></TableHead>
                                {paymentColumns.map((column) => (
                                  <TableHead
                                    key={column.id}
                                    className={`${getColumnAlignment(column.align)} ${column.sortable ? 'cursor-pointer hover:bg-muted/40' : ''}`}
                                    onClick={() => column.sortable && onPaymentSort(column.id)}
                                  >
                                    <div className={`flex items-center gap-1 ${column.align === 'right' ? 'justify-end' : column.align === 'center' ? 'justify-center' : ''}`}>
                                      {column.labelKey}
                                      {column.sortable && <SortIcon columnId={column.id} sortConfig={paymentSortConfig} />}
                                    </div>
                                  </TableHead>
                                ))}
                                <TableHead className="w-[50px]"></TableHead>
                              </TableRow>
                            </TableHeader>
                            <SortedPaymentsBody
                              payments={group.payments}
                              paymentColumns={paymentColumns}
                              paymentSortConfig={paymentSortConfig}
                              selectedIds={selectedIds}
                              onToggleSelect={onToggleSelect}
                      onOpenPay={onOpenPay}
                      onOpenCancel={onOpenCancel}
                      onOpenCharge={onOpenCharge}
                      renderPaymentCellValue={renderPaymentCellValue}
                      getColumnAlignment={getColumnAlignment}
                    />
                          </Table>
                        </div>
                      </TableCell>
                    </TableRow>
                  </CollapsibleContent>
                </>
              </Collapsible>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
